import React from 'react';
import { Box, Stepper, Step, StepLabel, StepConnector, Typography, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { CheckCircle, Cancel, Assignment, Work, Done } from '@mui/icons-material';

const QontoConnector = styled(StepConnector)(({ theme }) => ({
  '&.MuiStepConnector-line': {
    borderColor: theme.palette.divider,
  },
}));

const QontoStepIconRoot = styled('div')<{ ownerState: { active?: boolean; completed?: boolean } }>(
  ({ theme, ownerState }) => ({
    color: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[400],
    display: 'flex',
    height: 22,
    alignItems: 'center',
    ...(ownerState.active && {
      color: theme.palette.primary.main,
    }),
    '& .QontoStepIcon-completedIcon': {
      color: theme.palette.primary.main,
    },
    '& .QontoStepIcon-circle': {
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: 'currentColor',
    },
  }),
);

function QontoStepIcon(props: any) {
  const { active, completed, className } = props;

  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      {completed ? (
        <CheckCircle className="QontoStepIcon-completedIcon" />
      ) : (
        <div className="QontoStepIcon-circle" />
      )}
    </QontoStepIconRoot>
  );
}

interface JobStatusBarProps {
  status: 'draft' | 'pending' | 'in_progress' | 'completed' | 'cancelled';
  quoteStatus?: 'draft' | 'none' | 'sent' | 'responded' | 'counter' | 'accepted' | 'declined';
  canEdit: boolean;
}

export const JobStatusBar: React.FC<JobStatusBarProps> = ({ status, quoteStatus, canEdit }) => {
  // Define the steps based on the job flow
  const steps = [
    { label: 'Quote', key: 'quote', icon: <Assignment /> },
    { label: 'Assigned', key: 'assigned', icon: <Work /> },
    { label: 'In Progress', key: 'in_progress', icon: <Work /> },
    { label: 'Finished', key: 'completed', icon: <Done /> },
  ];

  // Determine current step based on status and quote status
  const getCurrentStep = () => {
    // If job is cancelled, show cancelled state
    if (status === 'cancelled') {
      return -1; // Special case for cancelled
    }

    // If in quote phase, show quote step
    if (quoteStatus && quoteStatus !== 'accepted') {
      return 0; // Quote step
    }

    // If quote is accepted or no quote phase, show based on job status
    switch (status) {
      case 'draft':
        return 0; // Quote step
      case 'pending':
        return 1; // Assigned step
      case 'in_progress':
        return 2; // In Progress step
      case 'completed':
        return 3; // Finished step
      default:
        return 0;
    }
  };

  const currentStep = getCurrentStep();

  // Get status color
  const getStatusColor = () => {
    if (status === 'cancelled') return 'error';
    if (status === 'completed') return 'success';
    if (status === 'in_progress') return 'primary';
    if (status === 'pending') return 'warning';
    return 'default';
  };

  // Get status label
  const getStatusLabel = () => {
    if (status === 'cancelled') return 'Cancelled';
    if (quoteStatus && quoteStatus !== 'accepted') {
      return `Quote: ${quoteStatus}`;
    }
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" component="div">
          Job Progress
        </Typography>
        <Chip 
          label={getStatusLabel()} 
          color={getStatusColor() as any}
          size="small"
        />
      </Box>

      {status === 'cancelled' ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
          <Cancel color="error" />
          <Typography variant="body2" color="error.main">
            This job has been cancelled by the agency.
          </Typography>
        </Box>
      ) : (
        <Stepper activeStep={currentStep} connector={<QontoConnector />}>
          {steps.map((step, index) => {
            const isCompleted = index < currentStep;
            const isActive = index === currentStep;
            
            return (
              <Step key={step.key}>
                <StepLabel
                  StepIconComponent={QontoStepIcon}
                  optional={
                    index === 0 && quoteStatus && quoteStatus !== 'accepted' ? (
                      <Typography variant="caption" color="text.secondary">
                        {quoteStatus === 'sent' ? 'Agency sent quote' : 
                         quoteStatus === 'responded' ? 'You responded' :
                         quoteStatus === 'counter' ? 'Counter offer made' :
                         quoteStatus === 'declined' ? 'Quote declined' :
                         'Quote pending'}
                      </Typography>
                    ) : index === 1 && status === 'pending' ? (
                      <Typography variant="caption" color="text.secondary">
                        Job assigned to you
                      </Typography>
                    ) : index === 2 && status === 'in_progress' ? (
                      <Typography variant="caption" color="text.secondary">
                        Work in progress
                      </Typography>
                    ) : index === 3 && status === 'completed' ? (
                      <Typography variant="caption" color="text.secondary">
                        Job completed
                      </Typography>
                    ) : null
                  }
                >
                  {step.label}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      )}

      {/* Status-based permissions info */}
      {canEdit && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2" color="info.main">
            <strong>Current Status:</strong> {getStatusLabel()}
            {status === 'pending' && (
              <span> - You can start working on this job and update the status to "In Progress"</span>
            )}
            {status === 'in_progress' && (
              <span> - You can upload files, send messages, and update the status to "Completed" when finished</span>
            )}
            {status === 'completed' && (
              <span> - This job is finished and locked. No further changes can be made.</span>
            )}
            {quoteStatus && quoteStatus !== 'accepted' && (
              <span> - You can respond to the quote, but cannot start work until the quote is accepted.</span>
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
